import { useEffect } from 'react';

/**
 * ⌘F / Ctrl+F puts the cursor in a dataset's search box.
 *
 * Claimed from the browser deliberately, the way the resource list claims it:
 * inside a dataset the thing a reader means by "find" is the dataset, not the
 * page chrome around it, and the browser's own find can only see the handful of
 * rows the grid has drawn.
 *
 * One listener for the whole app, and a stack rather than a set. Every mounted
 * grid used to add a listener of its own, so with two on screen — the row
 * picker's modal over a dataset page — both claimed the key, both called
 * `preventDefault`, and both focused their own box. The last one registered
 * happened to win, which was the right answer by accident: a modal is what the
 * reader is looking at. A stack says so on purpose, and it keeps the browser's
 * own find working when no dataset is mounted at all.
 */
const searchBoxes: React.MutableRefObject<HTMLInputElement | null>[] = [];

const onKeyDown = (event: KeyboardEvent): void => {
  if (event.key !== 'f' || !(event.metaKey || event.ctrlKey) || event.altKey) {
    return;
  }
  // The most recently mounted grid that still has a box to focus. A grid can be
  // registered and not yet rendered its input, which is not a reason to fall
  // through to the browser and leave the reader with two find bars.
  for (let index = searchBoxes.length - 1; index >= 0; index -= 1) {
    const input = searchBoxes[index]?.current;
    if (input !== null && input !== undefined) {
      event.preventDefault();
      input.focus();
      input.select();
      return;
    }
  }
};

export const useDatasetSearchHotkey = (
  inputRef: React.MutableRefObject<HTMLInputElement | null>
): void => {
  useEffect(() => {
    if (searchBoxes.length === 0) {
      window.addEventListener('keydown', onKeyDown);
    }
    searchBoxes.push(inputRef);
    return () => {
      const index = searchBoxes.lastIndexOf(inputRef);
      if (index !== -1) {
        searchBoxes.splice(index, 1);
      }
      // Handed back to the browser once the last dataset goes: a page with no
      // grid on it has nothing better to do with the key.
      if (searchBoxes.length === 0) {
        window.removeEventListener('keydown', onKeyDown);
      }
    };
  }, [inputRef]);
};

/** Drops every registration. Exists for tests, which share this module. */
export const resetDatasetSearchHotkey = (): void => {
  searchBoxes.length = 0;
  window.removeEventListener('keydown', onKeyDown);
};
